import React, { useCallback, useImperativeHandle, useMemo, useRef, useState } from 'react'
import BaseModal from '@/component/modal/BaseModal.tsx'
import Input from '@/component/input/Input.tsx'
import Select, { ISelectOption } from '@/component/input/Select.tsx'
import BorderButton from '@/component/button/BorderButton.tsx'
import SolidButton from '@/component/button/SolidButton.tsx'
import { IUser } from '@/api/user/getUsers.ts'
import { apiDisconnectUserDevice, apiUpdateUser } from '@/api/user/updateUser.ts'
import DeviceSerialCombobox from '@/component/input/DeviceSerialCombobox.tsx'
import { getApiErrorMessage } from '@/api/api.ts'
import { toast } from 'react-toastify'
import { useQueryClient } from '@tanstack/react-query'
import { QueryCacheKeys } from '@/api/queryKeys.ts'
import { useTranslation } from 'react-i18next'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { format } from 'date-fns'
import useAddressSearch, { IAddressSearchResult } from '@/util/hook/useAddressSearch.tsx'
import EmergencyTestRow from '@/pages/account/EmergencyTestRow.tsx'

export interface IProps {
    className?: string
    onUpdated?: () => void
}

const formatBirthDateForInput = (birthDate: string): string => {
    const date = new Date(birthDate)
    if (isNaN(date.getTime())) return birthDate
    const y = date.getFullYear()
    const m = String(date.getMonth() + 1).padStart(2, '0')
    const d = String(date.getDate()).padStart(2, '0')
    return `${y}-${m}-${d}`
}

/**
 * 사용자 입력(YYYYMMDD, YYYY-MM-DD 등)을 ISO 8601 형식으로 변환
 */
const toISO8601 = (input: string): string => {
    // 이미 ISO 8601이면 그대로 반환
    if (input.includes('T')) return input
    const cleaned = input.replace(/[^0-9]/g, '')
    if (cleaned.length !== 8) return input
    const y = cleaned.substring(0, 4)
    const m = cleaned.substring(4, 6)
    const d = cleaned.substring(6, 8)
    return new Date(`${y}-${m}-${d}T00:00:00.000Z`).toISOString()
}

const EditUserModal = ({ onUpdated }: IProps, ref: any) => {
    const modalRef = useRef<any>()
    const queryClient = useQueryClient()
    const { t } = useTranslation()
    const genderOptions = useMemo<ISelectOption[]>(() => [
        { id: '0', text: t('common.gender.male'), value: 0 },
        { id: '1', text: t('common.gender.female'), value: 1 }
    ], [t])

    const [editingUser, setEditingUser] = useState<IUser | null>(null)
    const [name, setName] = useState('')
    const [gender, setGender] = useState<number>(0)
    const [birthDate, setBirthDate] = useState('')
    const [contact, setContact] = useState('')
    const [address, setAddress] = useState('')
    const [addressDetail, setAddressDetail] = useState('')
    const [addressLatitude, setAddressLatitude] = useState(0)
    const [addressLongitude, setAddressLongitude] = useState(0)
    const [deviceSerial, setDeviceSerial] = useState('')
    const [initialDeviceSerial, setInitialDeviceSerial] = useState('')
    const [primaryRelation, setPrimaryRelation] = useState('')
    const [primaryContact, setPrimaryContact] = useState('')
    const [inspectorMemo, setInspectorMemo] = useState('')
    const [isLoading, setIsLoading] = useState(false)

    useImperativeHandle(ref, () => ({
        show: (user: IUser) => {
            setEditingUser(user)
            setName(user.name || '')
            setGender(user.gender ?? 0)
            setBirthDate(user.birthDate ? formatBirthDateForInput(user.birthDate) : '')
            setContact(user.contact || '')
            setAddress(user.address || '')
            setAddressDetail(user.addressDetail || '')
            setAddressLatitude(user.addressLatitude || 0)
            setAddressLongitude(user.addressLongitude || 0)
            setDeviceSerial(user.device?.device_serial || '')
            setInitialDeviceSerial(user.device?.device_serial || '')
            setPrimaryRelation(user.primaryRelation || '')
            setPrimaryContact(user.primaryContact || '')
            setInspectorMemo(user.inspectorMemo || '')
            modalRef.current.show()
        },
        close: () => {
            modalRef.current.close()
        }
    }))

    const onAddressSelected = useCallback((result: IAddressSearchResult) => {
        setAddress(result.address)
        setAddressLatitude(result.latitude)
        setAddressLongitude(result.longitude)
    }, [])

    const { openAddressSearch } = useAddressSearch(onAddressSelected)

    const onDisconnectDevice = async () => {
        if (!editingUser) return
        try {
            await apiDisconnectUserDevice(editingUser.id)
            setInitialDeviceSerial('')
            toast.success(t('account.protectee.device.disconnect.success'))
            await queryClient.invalidateQueries({
                queryKey: QueryCacheKeys.user.getUsersRoot()
            })
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('account.protectee.device.disconnect.fail')))
            throw error
        }
    }

    const onClickSubmit = async () => {
        if (!editingUser) return
        if (!name.trim()) {
            toast.error(t('account.protectee.edit.error.nameRequired'))
            return
        }
        if (!birthDate.trim()) {
            toast.error(t('account.protectee.add.error.birthDateRequired'))
            return
        }
        if (!contact.trim()) {
            toast.error(t('account.protectee.add.error.contactRequired'))
            return
        }
        if (!address.trim()) {
            toast.error(t('account.protectee.add.error.addressRequired'))
            return
        }
        setIsLoading(true)
        try {
            const { code } = await apiUpdateUser(editingUser.id, {
                name: name.trim(),
                gender,
                birthDate: toISO8601(birthDate.trim()),
                contact: contact.trim(),
                address: address.trim(),
                addressDetail: addressDetail.trim(),
                addressLatitude,
                addressLongitude,
                deviceSerial: deviceSerial.trim(),
                primaryRelation: primaryRelation.trim(),
                primaryContact: primaryContact.trim(),
                inspectorMemo: inspectorMemo.trim()
            })

            if (Number(code) >= 200 && Number(code) < 300) {
                toast.success(t('account.protectee.edit.success'))
                await queryClient.invalidateQueries({
                    queryKey: QueryCacheKeys.user.getUsersRoot()
                })
                onUpdated?.()
                modalRef.current?.close()
            } else {
                toast.error(t('account.protectee.edit.error.fail'))
            }
        } catch (error) {
            toast.error(getApiErrorMessage(error, t('account.protectee.edit.error.fail')))
        } finally {
            setIsLoading(false)
        }
    }

    return (
        <BaseModal
            ref={modalRef}
            showCloseButton={true}
            title={t('account.protectee.edit.title')}
            width={'600'}
        >
            <div className={'px-[30px] pb-[30px] max-h-[70vh] overflow-y-auto'}>
                <div className={'grid gap-[16px]'}>
                    {/* 이름 */}
                    <div>
                        <label className={'text-[14px] font-medium text-gray-700 mb-[6px] block'}>
                            {t('account.protectee.add.name')}{' '}
                            <span className={'text-red-500'}>*</span>
                        </label>
                        <Input
                            inputClassName={'w-full'}
                            value={name}
                            placeholder={t('account.protectee.add.name.placeholder')}
                            onChanged={(text) => setName(text)}
                        />
                    </div>

                    {/* 성별 / 생년월일 */}
                    <div className={'grid grid-cols-2 gap-[12px]'}>
                        <div>
                            <label
                                className={'text-[14px] font-medium text-gray-700 mb-[6px] block'}
                            >
                                {t('users.detail.gender')} <span className={'text-red-500'}>*</span>
                            </label>
                            <Select
                                className={'w-full'}
                                options={genderOptions}
                                selectedId={String(gender)}
                                onChanged={(option) => setGender(option.value)}
                            />
                        </div>
                        <div>
                            <label
                                className={'text-[14px] font-medium text-gray-700 mb-[6px] block'}
                            >
                                {t('users.detail.birthDate')}{' '}
                                <span className={'text-red-500'}>*</span>
                            </label>
                            <DatePicker
                                className={
                                    'w-full border border-gray2 rounded-[5px] px-[12px] py-[9px] text-[14px]'
                                }
                                wrapperClassName={'w-full'}
                                selected={birthDate ? new Date(birthDate) : null}
                                dateFormat={'yyyy-MM-dd'}
                                placeholderText={'1960-02-11'}
                                maxDate={new Date()}
                                showYearDropdown
                                scrollableYearDropdown
                                yearDropdownItemNumber={100}
                                onChange={(date: Date | null) =>
                                    setBirthDate(date ? format(date, 'yyyy-MM-dd') : '')
                                }
                            />
                        </div>
                    </div>

                    {/* 연락처 */}
                    <div>
                        <label className={'text-[14px] font-medium text-gray-700 mb-[6px] block'}>
                            {t('account.protectee.add.contact')}{' '}
                            <span className={'text-red-500'}>*</span>
                        </label>
                        <Input
                            inputClassName={'w-full'}
                            value={contact}
                            placeholder={t('account.protectee.add.contact.placeholder')}
                            onChanged={(text) => setContact(text)}
                        />
                    </div>

                    {/* 주소 */}
                    <div>
                        <label className={'text-[14px] font-medium text-gray-700 mb-[6px] block'}>
                            {t('users.detail.address')} <span className={'text-red-500'}>*</span>
                        </label>
                        <div className={'flex gap-[8px]'}>
                            <Input
                                className={'flex-1'}
                                inputClassName={'w-full'}
                                value={address}
                                disabled={true}
                                placeholder={t('account.protectee.add.address.placeholder')}
                            />
                            <BorderButton
                                size={'small'}
                                text={t('account.protectee.edit.address.search')}
                                className={'shrink-0'}
                                onClick={openAddressSearch}
                            />
                        </div>
                    </div>

                    {/* 상세주소 */}
                    <div>
                        <label className={'text-[14px] font-medium text-gray-700 mb-[6px] block'}>
                            {t('account.protectee.edit.addressDetail')}
                        </label>
                        <Input
                            inputClassName={'w-full'}
                            value={addressDetail}
                            placeholder={t('account.protectee.edit.addressDetail.placeholder')}
                            onChanged={(text) => setAddressDetail(text)}
                        />
                    </div>

                    {/* 디바이스 시리얼 */}
                    <div>
                        <label className={'text-[14px] font-medium text-gray-700 mb-[6px] block'}>
                            {t('account.protectee.table.deviceSerial')}
                        </label>
                        <DeviceSerialCombobox
                            value={deviceSerial}
                            initialSerial={initialDeviceSerial}
                            onChange={setDeviceSerial}
                            onDisconnect={onDisconnectDevice}
                        />
                    </div>

                    {editingUser && (
                        <EmergencyTestRow
                            protecteeId={editingUser.id}
                            protecteeName={editingUser.name}
                            hasDevice={Boolean(initialDeviceSerial)}
                            recentSession={editingUser.testSession}
                        />
                    )}

                    {/* 보호자 정보 */}
                    <div className={'grid grid-cols-2 gap-[12px]'}>
                        <div>
                            <label
                                className={'text-[14px] font-medium text-gray-700 mb-[6px] block'}
                            >
                                {t('account.protectee.add.primaryRelation')}
                            </label>
                            <Input
                                inputClassName={'w-full'}
                                value={primaryRelation}
                                placeholder={t('account.protectee.add.primaryRelation.placeholder')}
                                onChanged={(text) => setPrimaryRelation(text)}
                            />
                        </div>
                        <div>
                            <label
                                className={'text-[14px] font-medium text-gray-700 mb-[6px] block'}
                            >
                                {t('account.protectee.add.primaryContact')}
                            </label>
                            <Input
                                inputClassName={'w-full'}
                                value={primaryContact}
                                placeholder={t('account.protectee.add.primaryContact.placeholder')}
                                onChanged={(text) => setPrimaryContact(text)}
                            />
                        </div>
                    </div>

                    {/* 주의사항 */}
                    <div>
                        <label className={'text-[14px] font-medium text-gray-700 mb-[6px] block'}>
                            {t('users.detail.memo.title')}
                        </label>
                        <Input
                            inputClassName={'w-full'}
                            value={inspectorMemo}
                            placeholder={t('account.protectee.add.memo.placeholder')}
                            onChanged={(text) => setInspectorMemo(text)}
                        />
                    </div>
                </div>

                <p className={'text-[12px] text-gray-400 mt-[12px]'}>
                    <span className={'text-red-500'}>*</span>{' '}
                    {t('account.protectee.add.requiredFields')}
                </p>

                <div className={'flex gap-[10px] mt-[20px]'}>
                    <BorderButton
                        text={t('account.protectee.edit.cancel')}
                        className={'flex-1'}
                        onClick={() => modalRef?.current?.close()}
                    />
                    <SolidButton
                        text={t('account.protectee.edit.save')}
                        className={'flex-1'}
                        isLoading={isLoading}
                        onClick={onClickSubmit}
                    />
                </div>
            </div>
        </BaseModal>
    )
}

export default React.forwardRef(EditUserModal)
